import React from 'react';
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO } from "@/hooks/useSEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const Glossary = () => {
  useSEO({
    title: 'Glossary - Female Partner Calculator | Statistical Terms & Definitions',
    description: 'Comprehensive glossary of statistical and demographic terms used in partner compatibility calculations. Understand SMV, demographic data, and probability concepts.',
    keywords: 'statistical glossary, demographic terms, SMV definition, compatibility terms, dating calculator definitions',
    canonical: 'https://femalepartnercalculator.com/glossary'
  });

  const terms = [
    {
      term: "SMV (Sexual Market Value)",
      definition: "A concept from relationship psychology referring to an individual's attractiveness and desirability as a romantic partner, encompassing physical attractiveness, social status, personality traits, and other factors that influence dating success."
    },
    {
      term: "Compatibility Probability",
      definition: "The statistical likelihood of finding a partner who meets all specified demographic and preference criteria, calculated by multiplying individual factor probabilities."
    },
    {
      term: "Demographic Data",
      definition: "Statistical information about population characteristics including age, race, income, education, political affiliation, and other measurable attributes collected through government surveys and censuses."
    },
    {
      term: "Intersectional Analysis",
      definition: "Statistical method that accounts for how multiple demographic factors (age, race, etc.) interact and influence each other, rather than treating them as independent variables."
    },
    {
      term: "Representative Sample",
      definition: "A survey sample that accurately reflects the characteristics of the larger population being studied, ensuring statistical findings can be generalized."
    },
    {
      term: "Multiplicative Probability",
      definition: "Mathematical approach where individual probabilities are multiplied together to calculate combined likelihood, assuming independence between factors."
    },
    {
      term: "Census Bureau",
      definition: "U.S. government agency responsible for conducting the decennial census and ongoing demographic surveys, providing official population statistics."
    },
    {
      term: "NHANES",
      definition: "National Health and Nutrition Examination Survey conducted by the CDC, combining interviews and physical examinations to assess health and nutritional status."
    },
    {
      term: "NSFG",
      definition: "National Survey of Family Growth conducted by the CDC, collecting data on sexual behavior, pregnancy, and reproductive health among men and women in the United States."
    },
    {
      term: "Pew Research Center",
      definition: "Nonpartisan American think tank that conducts public opinion polling, demographic research, and social science research on various topics."
    },
    {
      term: "BMI (Body Mass Index)",
      definition: "A measurement using height and weight to determine if an individual is underweight, normal weight, overweight, or obese (BMI = weight(kg)/height(m)²)."
    },
    {
      term: "Statistical Significance",
      definition: "The likelihood that a research finding is not due to chance, typically requiring a 95% confidence level (p < 0.05) in academic research."
    },
    {
      term: "Sampling Error",
      definition: "The margin of error in survey results due to surveying only a sample of the population rather than the entire population."
    },
    {
      term: "American Community Survey (ACS)",
      definition: "Ongoing survey by the U.S. Census Bureau that gathers detailed demographic, social, economic, and housing information about American communities."
    },
    {
      term: "Current Population Survey (CPS)",
      definition: "Monthly survey conducted by the Census Bureau and Bureau of Labor Statistics, providing current statistics on employment, unemployment, and demographic characteristics."
    },
    {
      term: "General Social Survey (GSS)",
      definition: "Sociological survey that has tracked American attitudes and behaviors since 1972, providing longitudinal data on social trends."
    },
    {
      term: "Weighted Average",
      definition: "Statistical calculation that accounts for the relative importance or frequency of different data points, used when combining multiple demographic categories."
    },
    {
      term: "Base Population",
      definition: "The total number of adult women (approximately 168 million) in the United States used as the foundation for calculating absolute numbers of compatible individuals."
    },
    {
      term: "Factor Breakdown",
      definition: "Analysis showing how each individual preference (age, race, politics, etc.) contributes to the overall compatibility probability calculation."
    },
    {
      term: "Temporal Lag",
      definition: "The delay between when demographic data is collected and when it's published, typically 1-2 years for government surveys."
    },
    {
      term: "Geographic Variation",
      definition: "Differences in demographic characteristics between different regions, states, or metropolitan areas that can affect local dating markets."
    },
    {
      term: "Dating Market",
      definition: "The pool of available potential romantic partners in a given area or demographic group, influenced by local population characteristics."
    },
    {
      term: "Self-Selection Bias",
      definition: "Statistical bias that occurs when survey participants choose to participate, potentially making the sample unrepresentative of the broader population."
    },
    {
      term: "Confidence Interval",
      definition: "A range of values that likely contains the true population parameter, typically expressed with 95% confidence (e.g., 'between 23% and 27%')."
    },
    {
      term: "Cross-Tabulation",
      definition: "Statistical technique showing the relationship between two or more demographic variables, revealing how factors interact with each other."
    },
    {
      term: "Educational Attainment",
      definition: "The highest level of education completed by an individual, typically categorized as high school or less, some college, bachelor's degree, or graduate degree."
    },
    {
      term: "Educational Stratification",
      definition: "The process by which educational credentials are distributed across different demographic groups, affecting social and dating market dynamics."
    },
    {
      term: "Conditional Probabilities by Education",
      definition: "Statistical calculations that account for how educational level correlates with other demographic factors like age, race, and political affiliation."
    },
    {
      term: "Age-Stratified Education",
      definition: "Educational attainment data broken down by age groups, reflecting generational differences in college completion rates and degree attainment."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              Glossary of Terms
            </h1>
            <p className="text-xl text-muted-foreground">
              Key statistical and demographic terms used in our compatibility calculations
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Terms & Definitions</CardTitle>
              <CardDescription>
                Understanding the statistical and demographic concepts behind our calculations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {terms.map((item, index) => (
                  <div key={index} className="border-b border-border pb-4 last:border-b-0">
                    <h3 className="font-semibold text-lg mb-2">{item.term}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.definition}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <p className="text-muted-foreground">
              Need more details? Check our <a href="/methodology" className="text-primary hover:underline">methodology page</a> or <a href="/data-sources" className="text-primary hover:underline">data sources</a>
            </p>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Glossary;